/**
 * Dialog for creating a new teardown script workflow.
 *
 * @module CreateTeardownScriptDialog
 */
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DialogTrigger, Dialog, DialogContent, DialogFooter, Button, TextField, Flex, Text } from '@chatsdet/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkflowMutations } from '../../hooks/useWorkflowMutations';

interface CreateTeardownScriptDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function CreateTeardownScriptDialog({ isOpen, onOpenChange }: CreateTeardownScriptDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentOrgId = user?.orgId ?? null;
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  const { createWorkflowMutation } = useWorkflowMutations({
    currentOrgId,
    workflowId: null,
  });

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      const result = await createWorkflowMutation.mutateAsync({
        name: trimmedName,
        description: description.trim() || undefined,
        status: 'draft',
        flowType: 'teardown',
      });

      const newWorkflowId = result?.workflow?.id;
      if (newWorkflowId) {
        onOpenChange(false);
        setName('');
        setDescription('');
        navigate({
          to: '/flows/$workflowId',
          params: { workflowId: newWorkflowId },
        });
      }
    } catch (error) {
      console.error('Failed to create teardown script', error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setName('');
      setDescription('');
    }
    onOpenChange(open);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="sm">
      <span />
      <Dialog title="New Teardown Script">
        <DialogContent>
          <Flex direction="column" gap="md">
            <Text size="sm" className="text-muted-foreground">
              Teardown scripts clean up seed data after test suites complete.
            </Text>
            <TextField
              label="Name"
              value={name}
              onChange={setName}
              placeholder="e.g. Delete test users"
              autoFocus
            />
            <TextField
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="Optional description"
            />
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={!name.trim() || createWorkflowMutation.isPending}
          >
            {createWorkflowMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
